/// <reference types="../../typings/zaptic-types" />
import type { LanguageCode, SupportedLanguage, SupportedLocale } from 'zaptic-types'
import { assumeExhaustion, createStringEnum } from '../lib/typeHelpers.no-deps'
import { assert, type Equals } from 'tsafe'

export type { SupportedLanguage, SupportedLocale, LanguageCode }

export const defaultLanguage = 'en-GB'

export interface OneLanguageTranslations {
    [key: string]: string | OneLanguageTranslations
}

export interface TranslationTree<T> {
    [key: string]: TranslationNode<T>
}

export type TranslationNode<T> = TranslationTree<T> | T
export interface TranslationMap extends TranslationTree<TranslationEntry> {}

export type TranslationEntry = { [LANG in SupportedLanguage]?: string | null } & {
    context: string
    [defaultLanguage]: string
}

export type TranslationValue = TranslationMap | TranslationEntry

export function isTranslationEntry(value: TranslationValue): value is TranslationEntry {
    return value.hasOwnProperty(defaultLanguage) && typeof value[defaultLanguage] === 'string'
}

//  When adding a new language please also update:
//   - typings/zaptic-types/index.d.ts
//   - portal/apps/src/Shared/Langauge.elm

export const supportedLanguages = createStringEnum(
    defaultLanguage,
    'de-DE',
    'pt-BR',
    'pt-PT',
    'fr-FR',
    'es-ES',
    'it-IT',
    'zh-CN',
    'ru-RU',
    'el-GR',
    'sr-SP',
    'lv-LV',
    'lt-LT',
    'et-EE',
    'bg-BG',
    'km-KH',
    'ja-JP',
    'uk-UA',
    'vi-VN',
    'nl-NL',
    'pl-PL',
    'cs-CZ',
    'sk-SK',
    'hu-HU',
    'ro-RO',
    'hr-HR',
    'ms-MY',
    'fr-CA',
    'no-NO',
    'tr-TR',
    'nl-BE',
    'kk-KZ',
    'id-ID',
    'fil-PH',
    'ko-KR',
    'sr-Latn-RS'
)

export const supportedLocales = createStringEnum(...supportedLanguages, 'en-US')

export const languageCodes = createStringEnum(
    'enGB',
    'deDE',
    'ptBR',
    'ptPT',
    'frFR',
    'esES',
    'itIT',
    'zhCN',
    'ruRU',
    'elGR',
    'srSP',
    'lvLV',
    'ltLT',
    'etEE',
    'bgBG',
    'kmKH',
    'jaJP',
    'ukUA',
    'viVN',
    'nlNL',
    'plPL',
    'csCZ',
    'skSK',
    'huHU',
    'roRO',
    'hrHR',
    'msMY',
    'frCA',
    'noNO',
    'trTR',
    'nlBE',
    'kkKZ',
    'idID',
    'filPH',
    'koKR',
    'srLatnRS'
)

// Ensure that these match the types exhaustively
assert<Equals<SupportedLanguage, (typeof supportedLanguages)[number]>>()
assert<Equals<SupportedLocale, (typeof supportedLocales)[number]>>()
assert<Equals<LanguageCode, (typeof languageCodes)[number]>>()

export type Translation<T> = { [code in LanguageCode]: T } & { context?: string }

/**
 * Translate from supported language to language code, basically just removes the hyphen
 * This is so they can be used as object paths without quotation marks
 * Also defaults to enGB if undefined
 */
export function languageToLanguageCode(language: SupportedLanguage): LanguageCode {
    switch (language) {
        case 'en-GB':
            return 'enGB'
        case 'de-DE':
            return 'deDE'
        case 'pt-BR':
            return 'ptBR'
        case 'pt-PT':
            return 'ptPT'
        case 'fr-FR':
            return 'frFR'
        case 'es-ES':
            return 'esES'
        case 'it-IT':
            return 'itIT'
        case 'zh-CN':
            return 'zhCN'
        case 'ru-RU':
            return 'ruRU'
        case 'el-GR':
            return 'elGR'
        case 'sr-SP':
            return 'srSP'
        case 'lv-LV':
            return 'lvLV'
        case 'lt-LT':
            return 'ltLT'
        case 'et-EE':
            return 'etEE'
        case 'bg-BG':
            return 'bgBG'
        case 'km-KH':
            return 'kmKH'
        case 'ja-JP':
            return 'jaJP'
        case 'uk-UA':
            return 'ukUA'
        case 'vi-VN':
            return 'viVN'
        case 'nl-NL':
            return 'nlNL'
        case 'pl-PL':
            return 'plPL'
        case 'cs-CZ':
            return 'csCZ'
        case 'sk-SK':
            return 'skSK'
        case 'hu-HU':
            return 'huHU'
        case 'ro-RO':
            return 'roRO'
        case 'hr-HR':
            return 'hrHR'
        case 'ms-MY':
            return 'msMY'
        case 'fr-CA':
            return 'frCA'
        case 'no-NO':
            return 'noNO'
        case 'tr-TR':
            return 'trTR'
        case 'nl-BE':
            return 'nlBE'
        case 'kk-KZ':
            return 'kkKZ'
        case 'id-ID':
            return 'idID'
        case 'fil-PH':
            return 'filPH'
        case 'ko-KR':
            return 'koKR'
        case 'sr-Latn-RS':
            return 'srLatnRS'
        default:
            assumeExhaustion(language)
            return 'enGB'
    }
}

/**
 * Opting to put all the translation inline and relying on runing find-all-references
 * on this function in order to find them.
 */
export function pickTranslation<T>(language: SupportedLanguage, translation: Translation<T>): T {
    const languageCode = languageToLanguageCode(language)
    return translation[languageCode] || translation.enGB
}

export function languageName(language: SupportedLanguage): string {
    switch (language) {
        case 'en-GB':
            return 'English'
        case 'de-DE':
            return 'Deutsch'
        case 'pt-BR':
            return 'Português (BR)'
        case 'pt-PT':
            return 'Português (PT)'
        case 'fr-FR':
            return 'Français'
        case 'es-ES':
            return 'Español'
        case 'it-IT':
            return 'Italiano'
        case 'zh-CN':
            return '简体中文'
        case 'ru-RU':
            return 'Русский'
        case 'el-GR':
            return 'Ελληνικά'
        case 'sr-SP':
            return 'Српски'
        case 'lv-LV':
            return 'latviešu valoda'
        case 'lt-LT':
            return 'lietuvių'
        case 'et-EE':
            return 'eesti keel'
        case 'bg-BG':
            return 'български'
        case 'km-KH':
            return 'ខ្មែរ'
        case 'ja-JP':
            return '日本語'
        case 'uk-UA':
            return 'Українська'
        case 'vi-VN':
            return 'Tiếng việt'
        case 'nl-NL':
            return 'Nederlandse'
        case 'pl-PL':
            return 'polski'
        case 'cs-CZ':
            return 'český'
        case 'sk-SK':
            return 'slovenský'
        case 'hu-HU':
            return 'magyar'
        case 'ro-RO':
            return 'Română'
        case 'hr-HR':
            return 'Hrvatski'
        case 'ms-MY':
            return 'Malay'
        case 'fr-CA':
            return 'français canadien'
        case 'no-NO':
            return 'Norsk'
        case 'tr-TR':
            return 'Türkçe'
        case 'nl-BE':
            return 'Vlaams'
        case 'kk-KZ':
            return 'Қазақша'
        case 'id-ID':
            return 'Bahasa Indonesia'
        case 'fil-PH':
            return 'Filipino'
        case 'ko-KR':
            return '한국어'
        case 'sr-Latn-RS':
            return 'Srpski'
        default:
            assumeExhaustion(language)
            return 'enGB'
    }
}

export function isOneOfSupportedLanguages(language: string): language is SupportedLanguage {
    // We have to assert here that this is a list of strings, since includes is too narrowly typed
    // That is a big issue actually -> https://github.com/microsoft/TypeScript/issues/26255
    return (supportedLanguages as readonly string[]).includes(language)
}
